import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { MAX_BULK_ORDERS, orderBulkJobSchema, orderDocumentSchema } from "./order-workflow.js";
import { orderChannelSchema, orderDetailSchema, orderStatusSchema } from "./orders.js";
/** Tags, assignee, notes, saved views, bulk status and printing (O-06, O-09, O-10; docs/specs/sales.md "B-33"). */
const tag = { tag: "sales" };
const version = z.int().min(1);
export const MAX_ORDER_TAGS = 10;
const tagName = z.string().trim().toLowerCase().min(1).max(40);
/** `PATCH /v1/orders/{id}/labels`: tags and assignee, in any status (they change no money or stock). */
export const updateOrderLabelsRequestSchema = z
    .object({
    tags: z
        .array(tagName)
        .max(MAX_ORDER_TAGS)
        .transform((tags) => [...new Set(tags)])
        .optional(),
    assigneeId: z.uuid().nullable().optional(),
    version,
})
    .strict();
export const addOrderNoteRequestSchema = z.object({ body: z.string().trim().min(1).max(1000) }).strict();
export const orderAssigneeListSchema = z.object({
    data: z.array(z.object({ id: z.uuid(), name: z.string() })),
});
/** The list filters a saved view keeps (O-09); the same names as `GET /v1/orders`. */
export const orderViewFiltersSchema = z
    .object({
    status: orderStatusSchema.optional(),
    channel: orderChannelSchema.optional(),
    locationId: z.uuid().optional(),
    tag: tagName.optional(),
    assigneeId: z.union([z.uuid(), z.literal("none")]).optional(),
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
})
    .strict();
export const orderViewSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    filters: orderViewFiltersSchema,
    /** Shared views are listed for everyone in the tenant; others only for their owner. */
    shared: z.boolean(),
    ownerId: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
});
export const orderViewListSchema = z.object({ data: z.array(orderViewSchema) });
export const createOrderViewRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(60),
    filters: orderViewFiltersSchema,
    shared: z.boolean().default(false),
})
    .strict();
export const bulkStatusRequestSchema = z
    .object({
    orderIds: z.array(z.uuid()).min(1).max(MAX_BULK_ORDERS),
    /** One step for every order; `partially_delivered` needs quantities and is not offered in bulk. */
    to: z.enum(["confirmed", "packed", "shipped", "delivered", "returned", "cancelled"]),
})
    .strict();
export const printOrdersRequestSchema = z
    .object({ orderIds: z.array(z.uuid()).min(1).max(MAX_BULK_ORDERS), document: orderDocumentSchema })
    .strict();
// Endpoints ----------------------------------------------------------------------------------------------------------
export const updateOrderLabels = defineEndpoint({
    ...tag,
    name: "updateOrderLabels",
    method: "PATCH",
    path: "/v1/orders/{id}/labels",
    access: { permission: "orders.manage" },
    summary: "Set an order's tags and assignee",
    params: idParamsSchema,
    body: updateOrderLabelsRequestSchema,
    status: 200,
    response: orderDetailSchema,
    errors: ["CONFLICT"],
});
export const addOrderNote = defineEndpoint({
    ...tag,
    name: "addOrderNote",
    method: "POST",
    path: "/v1/orders/{id}/notes",
    access: { permission: "orders.view" },
    summary: "Add an internal note to an order",
    params: idParamsSchema,
    body: addOrderNoteRequestSchema,
    status: 201,
    response: orderDetailSchema,
});
export const listOrderAssignees = defineEndpoint({
    ...tag,
    name: "listOrderAssignees",
    method: "GET",
    path: "/v1/order-assignees",
    access: { permission: "orders.view" },
    summary: "People who can be assigned orders (they hold orders.manage)",
    status: 200,
    response: orderAssigneeListSchema,
});
export const listOrderViews = defineEndpoint({
    ...tag,
    name: "listOrderViews",
    method: "GET",
    path: "/v1/order-views",
    access: { permission: "orders.view" },
    summary: "The caller's saved order views and the shared ones",
    status: 200,
    response: orderViewListSchema,
});
export const createOrderView = defineEndpoint({
    ...tag,
    name: "createOrderView",
    method: "POST",
    path: "/v1/order-views",
    access: { permission: "orders.view" },
    summary: "Save the current order filters as a view",
    body: createOrderViewRequestSchema,
    status: 201,
    response: orderViewSchema,
});
export const deleteOrderView = defineEndpoint({
    ...tag,
    name: "deleteOrderView",
    method: "DELETE",
    path: "/v1/order-views/{id}",
    access: { permission: "orders.view" },
    summary: "Delete one of the caller's saved views",
    params: idParamsSchema,
    status: 204,
});
export const bulkOrderStatus = defineEndpoint({
    ...tag,
    name: "bulkOrderStatus",
    method: "POST",
    path: "/v1/orders/bulk-status",
    access: { permission: "orders.manage" },
    summary: "Move many orders one step as a job; each order's result is kept",
    body: bulkStatusRequestSchema,
    idempotent: true,
    status: 202,
    response: orderBulkJobSchema,
});
export const printOrders = defineEndpoint({
    ...tag,
    name: "printOrders",
    method: "POST",
    path: "/v1/orders/print",
    access: { permission: "orders.view" },
    summary: "Invoices (A4 or 80 mm), packing slips or a pick list for up to 200 orders in one PDF, as a job",
    body: printOrdersRequestSchema,
    status: 202,
    response: orderBulkJobSchema,
});
//# sourceMappingURL=order-desk.js.map